import _ from 'lodash';
import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import { COLUMN_TYPES } from './DataTableColumns';
import format, {
    getUnits,
    dateTimeFormatter,
    getDefaultDateFormat,
    DEFAULT_INVALID_VALUE,
} from '../../utilities/FormattingUtilities';
import { getSystemTime } from '../../stores/SystemTimeStore';

export const FILE_TYPE_STRING = 'attachment/csv;charset=utf-8';
export const EXPORT_TOO_LARGE = 'EXPORT_TOO_LARGE';

const EOL = '\r\n';
const SEPARATOR = ',';
const CLEAN_REGX = /\r|\n|\t/g;

const ROW_START = 0;
const PAGE_SIZE = 500;
const MAX_RECORDS = 100000;

function buildCsv(columns, loader, sorters, filters, params) {
    let pageSize = _.get(params, 'csvPageSize', PAGE_SIZE);
    if (pageSize > MAX_RECORDS) {
        // eslint-disable-next-line no-console
        console.warn(`csvPageSize (${pageSize}) exceeds MAX_RECORDS (${MAX_RECORDS}); clamped to MAX_RECORDS.`);
        pageSize = MAX_RECORDS;
    }
    const filteredColumns = getCSVColumns();

    function pullRecordCountFromResponse(response) {
        return _.get(response, 'length', []);
    }

    function pullDataFromResponse(response) {
        if (_.isArray(response)) {
            return response;
        }

        return _.get(response, 'rows', []);
    }

    function getSubsequentPagesParallel(requestParams, response, responseData) {
        let rowStart = _.get(requestParams, 'rowStart', 0);
        const promises = [];
        const totalRecordCount = pullRecordCountFromResponse(response);

        // We've already fetched one page back in the buildData
        //  function because we needed to get the total record count
        //  increment the rowStart so we can get the next page.
        rowStart += pageSize;

        // Each loop with create a promise that gets data for the next page
        //  until we've either pulled all the pages or we've hit our client side limit (MAX_RECORDS).
        //  Each response merges the array from this page with our total set of data (responseData)
        while (rowStart < totalRecordCount && rowStart < MAX_RECORDS) {
            const thisRowStart = rowStart;
            promises.push(
                Promise.resolve()
                    .then(() => loader({ filters, sorters, start: thisRowStart, limit: pageSize }))
                    .then((result) => responseData.push(...pullDataFromResponse(result)))
            );
            rowStart += pageSize;
        }

        return Promise.all(promises).then(() => responseData);
    }

    async function getSubsequentPagesSequential(response, responseData, requestParams, useStartInsteadOfCursor) {
        const totalRecordCount = pullRecordCountFromResponse(response);
        let { cursor } = response;

        // First page already in responseData
        let count = pageSize;
        let rowStart = _.get(requestParams, 'rowStart', 0) + pageSize;

        /* eslint-disable no-await-in-loop */
        const maxRecords = _.min([totalRecordCount, MAX_RECORDS]);
        while (count < maxRecords) {
            const loaderParams = useStartInsteadOfCursor
                ? { filters, sorters, start: rowStart, limit: pageSize }
                : { filters, sorters, limit: pageSize, cursor };

            const result = await loader(loaderParams);
            responseData.push(...pullDataFromResponse(result));

            if (useStartInsteadOfCursor) {
                rowStart += pageSize;
            } else {
                cursor = result.cursor;
            }

            count += pageSize;
        }
        /* eslint-enable no-await-in-loop */

        return responseData;
    }

    function getSubsequentPages(response, requestParams) {
        const responseData = pullDataFromResponse(response) || [];

        // If we don't have data for the first page then we
        //  don't have data for the other pages...
        if (_.isEmpty(responseData)) {
            return responseData;
        }

        const forceSequential = _.get(requestParams, 'csvSequential', false);
        const hasCursor = !_.isNil(response.cursor);

        // if forceSequential is true but no cursor, use start for pagination
        const useStart = forceSequential && !hasCursor;

        if (forceSequential || hasCursor) {
            return getSubsequentPagesSequential(response, responseData, requestParams, useStart);
        }

        return getSubsequentPagesParallel(requestParams, response, responseData);
    }

    function cleanForCSV(value) {
        if (!_.isString(value)) {
            return value;
        }

        // Since csv is comma separated now empty string is better for importing than dashes
        if (!_.isEmpty(value) && _.isString(value) && value.replace(/['"]+/g, '') === DEFAULT_INVALID_VALUE) {
            return '';
        }

        // Basic cleaning for CSV, carriage returns, tabs, anything else that may cause issues.
        let cleanedValue = value.replace(CLEAN_REGX, ' ');

        // If we have anything escaped for HTML, unescape it.
        cleanedValue = cleanedValue.replace(/&amp;/g, '&');
        cleanedValue = cleanedValue.replace(/&lt;/g, '<');
        cleanedValue = cleanedValue.replace(/&gt;/g, '>');
        cleanedValue = cleanedValue.replace(/&quot;/g, '"');
        cleanedValue = cleanedValue.replace(/&#39;/g, "'");

        return cleanedValue;
    }

    function getColumnValue(aColumn, rowOfData) {
        if (_.isFunction(aColumn.mapper)) {
            return aColumn.mapper(rowOfData, aColumn);
        }

        return _.get(rowOfData, aColumn.mapping);
    }

    function addQuotes(valueFormatted) {
        if (!_.isEmpty(valueFormatted) && _.isString(valueFormatted)) {
            return `"${valueFormatted.replace(/"/g, '""')}"`;
        }
        return valueFormatted;
    }

    function getFormatter(aColumn) {
        return aColumn.formatter;
    }

    function formatColumnValue(aColumn, rowOfData) {
        const colFormatterConfig = _.merge({ displayUnits: false }, aColumn);
        const columnValue = getColumnValue(aColumn, rowOfData);
        const objectFormatter = 'type';

        let formatted = columnValue;
        const aFormatter = getFormatter(aColumn);

        // If the standard formatter isn't good enough for CSV
        if (_.isFunction(aColumn.csvFormatter)) {
            formatted = aColumn.csvFormatter(columnValue, rowOfData, colFormatterConfig);
        } else if (_.isString(aColumn.formatter)) {
            formatted = format(columnValue, { type: aColumn.formatter, displayUnits: false });
        } else if (_.has(aColumn.formatter, objectFormatter)) {
            formatted = format(columnValue, _.merge({}, aColumn.formatter, { displayUnits: false }));
        } else if (_.isFunction(aFormatter)) {
            // If there is a formatter, use it.
            formatted = aFormatter(columnValue, rowOfData, colFormatterConfig);
        }

        return formatGenericValue(formatted);
    }

    function formatGenericValue(value) {
        if (!value || !_.isString(value)) {
            return value;
        }

        return value.replace(/^(\d+)\s*\/\s*(\d+)$/g, '$1 of $2');
    }

    function buildRow(rowOfData) {
        return _.map(filteredColumns, (aColumn) => cleanForCSV(addQuotes(formatColumnValue(aColumn, rowOfData)))).join(
            SEPARATOR
        );
    }

    function getColumnType(aColumn) {
        return _.get(aColumn, 'type', '');
    }

    function includeColumn(aColumn) {
        const exclude = [
            COLUMN_TYPES.SPARKLINE,
            COLUMN_TYPES.BARCHART,
            COLUMN_TYPES.SELECTION,
            COLUMN_TYPES.FLEX,
            COLUMN_TYPES.STATUSTREND,
        ];

        if (!aColumn || aColumn.uiOnly || aColumn.pdfOnly) {
            return false;
        }

        return _.indexOf(exclude, getColumnType(aColumn)) < 0;
    }

    function getCSVColumns() {
        return _.filter(columns, includeColumn);
    }

    function buildData() {
        // These are the params that are setup in the collection... (entityFields: ..., rowLimit: 100, sort: ...)
        //  Clone them so we don't screw up the UI
        // eslint-disable-next-line prefer-const
        let requestParams = _.cloneDeep(params || {});
        // Overide start and limit as we want as much as we can.
        _.set(requestParams, 'rowStart', ROW_START);
        _.set(requestParams, 'rowLimit', pageSize);
        // Set this to true so that the query.service can make decisions based on it being a CSV.
        _.set(requestParams, 'isCSV', true);

        let expectedRowCount = 0;
        let hasCursor = false;

        return Promise.resolve()
            .then(() =>
                loader({
                    filters,
                    sorters,
                    start: requestParams.rowStart,
                    limit: requestParams.rowLimit,
                })
            )
            .then((response) => {
                hasCursor = !_.isNil(response.cursor);
                expectedRowCount = pullRecordCountFromResponse(response);
                if (expectedRowCount > MAX_RECORDS) {
                    const err = new Error(
                        `The export contains too many records (${expectedRowCount}). The maximum allowed is ${MAX_RECORDS}. ` +
                            `Please narrow down your filters and try again.`
                    );
                    err.code = EXPORT_TOO_LARGE;
                    throw err;
                }
                return getSubsequentPages(response, requestParams);
            })
            .then((responseData) => {
                const rows = _.map(responseData, (aRowOfData) => buildRow(aRowOfData));

                if (rows.length !== expectedRowCount && hasCursor) {
                    // If there's no cursor, it's not a global view so no use mentioning PLMs
                    rows.push(
                        i18n._(
                            t`One or more Local Managers were unresponsive during the CSV export process; some data may be missing or incomplete.`
                        )
                    );
                }

                return rows.join(EOL);
            })
            .then((csvData) => {
                return `${buildHeader()}${EOL}${csvData}`;
            });
    }

    function getColumnUnits(columnConfig) {
        const colFormatterConfig = _.merge({ onlyUnits: true }, columnConfig);
        let columnUnit;
        if (getColumnType(columnConfig) === COLUMN_TYPES.NUMERIC) {
            const aFormatter = columnConfig.csvFormatter || getFormatter(columnConfig);
            if (_.isFunction(aFormatter)) {
                columnUnit = aFormatter(null, colFormatterConfig);
            } else if (_.isString(aFormatter) || _.isObject(aFormatter)) {
                columnUnit = getUnits(aFormatter);
            }
        }
        return columnUnit && columnUnit !== DEFAULT_INVALID_VALUE ? ` (${columnUnit})` : '';
    }

    function getHeaderTitle(aColumn) {
        const title = aColumn.csvTitle || aColumn.title;

        if (_.isObject(title)) {
            return i18n._(title);
        }

        return title;
    }

    function buildHeader() {
        if (!filteredColumns || !_.isArray(filteredColumns)) {
            return [];
        }

        return _.map(filteredColumns, (aColumn) => {
            const columnUnit = getColumnUnits(aColumn);
            return `"${cleanForCSV(getHeaderTitle(aColumn))}${columnUnit}"`;
        }).join(SEPARATOR);
    }

    return buildData();
}

export function buildCsvFileName() {
    function buildFileName() {
        const emptyValue = 'ngp-exported-list';

        const lastBreadCrumb = findTitle() || emptyValue;
        return buildFullFileName(lastBreadCrumb.split(' ').join('-'));
    }

    function findTitle() {
        const pageTitleElementId = 'page-title';
        const titleElement = document.getElementById(pageTitleElementId);
        if (!titleElement) {
            return null;
        }

        return titleElement.innerText.replace(/\r+|\n+/g, '');
    }

    function buildFullFileName(name) {
        return _.toLower(`${name}-${getFileTimeStamp()}.csv`);
    }

    /**
     * Put in a readable date
     * @return {string}
     */
    function getFileTimeStamp() {
        return dateTimeFormatter(getSystemTime(), { toFormat: getDefaultDateFormat() }).replace(/\s+|:/g, '-');
    }

    return buildFileName();
}

export default buildCsv;
